import { useTranslation } from 'react-i18next';
import { FunctionComponent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Checkbox, Col, Divider, Flex, Form, InputNumber, Row, Skeleton, Spin, Typography } from 'antd';
import { useDebouncedCallback } from 'use-debounce';
import dayjs from 'dayjs';

import DatePickerElement from 'components/datePicker/datePicker';
import StatusComponent from 'components/status/status';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import DisabledElement from 'components/disabledElement/disabledElement';
import FormNumberElement from 'components/formNumberElement/formNumberElement';
import VerificationModal from 'modules/main/actions/verificationModal';
import ActionCostComponent from 'modules/main/actions/cost';
import Icon from 'utils/icon';

import {
	onCheckCapitalRaiseByUnits,
	onCheckCapitalRaiseRatio,
	onCheckDateFields,
	onCheckEligibilityDate,
	onCreateProfitCapitalization,
	onUpdateProfitCapitalization,
} from '../services/profitCapitalization';
import NotificationService from 'core/services/notification';
import { onGetRaiseCapital } from '../services/raiseCapitals';
import { mapResponseErrors } from 'utils/mapResponseErrors';

import { ServiceCost, VerificationPayload } from 'modules/main/actions/commonModels';
import { ProfitCapitalization, ProfitCapitalizationCreatePayload } from '../models/profitCapitalization';
import NumberElement from 'components/number/number';
import InfoTooltip from 'components/info/info';

// components
import SuspendedTooltip from 'components/suspendedTooltip/suspendedTooltip';

interface ProfitCapitalizationFormProps {
	serviceCost?: ServiceCost;
	editMode?: boolean;
	profitCapitalization?: ProfitCapitalization;
}

const ProfitCapitalizationForm: FunctionComponent<ProfitCapitalizationFormProps> = ({ serviceCost, editMode, profitCapitalization }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { id } = useParams<{ id: string }>();

	const [form] = Form.useForm();

	const capitalRaiseWatch = Form.useWatch('capital_raise_ratio', form);

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [hiddenDateFields, setHiddenDateFields] = useState<boolean>(!editMode);

	const [isCheckingEligibilityDate, setIsCheckingEligibilityDate] = useState<boolean>(false);
	const [isValidEligibilityDate, setIsValidEligibilityDate] = useState<boolean>(!!editMode);

	const [isCheckingCapitalRaiseRatio, setIsCheckingCapitalRaiseRatio] = useState<boolean>(false);
	const [totalCapitalRaise, setTotalCapitalRaise] = useState<number>(0);
	const [actualCapitalRaiseRatio, setActualCapitalRaiseRatio] = useState<number>(0);

	const [acknowledge, setAcknowledge] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [verificationModalOpen, setVerificationModalOpen] = useState<boolean>(false);

	useEffect(() => {
		getData();
	}, []);

	useEffect(() => {
		if (profitCapitalization) {
			form.setFieldsValue({
				capital_raise_ratio: profitCapitalization.action.capital_raise_ratio,
				eligibility_date: dayjs(profitCapitalization.action.eligibility_date),
				distribution_date: dayjs(profitCapitalization.action.distribution_date),
				number_of_issued_units: +profitCapitalization.action.number_of_issued_units.replaceAll(/,/g, ''),
			});

			setTotalCapitalRaise(Number(profitCapitalization.action.total_capital_raise.replaceAll(/,/g, '')));
			setActualCapitalRaiseRatio(+profitCapitalization.action.capital_raise_ratio);
		}
	}, [profitCapitalization]);

	const getData = async () => {
		try {
			setIsLoading(true);

			await Promise.all([checkHiddenDateFields()]);

			setIsLoading(false);
		} catch (error: any) {
			setIsLoading(false);
			const message = error?.response?.data?.message || t('COMMON.FETCH_ERROR');
			NotificationService.error(t('COMMON.ERROR'), message);

			navigate('/dashboard/actions/capitals');
		}
	};

	const checkHiddenDateFields = async () => {
		try {
			if (editMode) return;

			const response = await onCheckDateFields();
			setHiddenDateFields(response.hide_date_fields);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR', t('COMMON.FETCH_ERROR')));
			navigate('/dashboard/actions/capitals');
		}
	};

	// const modifyCount = (operation: 'plus' | 'minus') => {
	// 	const current = +(form.getFieldValue('capital_raise_ratio') || 0);
	// 	let updated = current;

	// 	if (operation === 'plus') {
	// 		updated = +(current + 1).toFixed(4);
	// 	}

	// 	if (operation === 'minus' && current > 0) {
	// 		updated = +(current - 1).toFixed(4);
	// 	}

	// 	form.setFieldValue('capital_raise_ratio', updated);
	// 	checkCapitalRaiseRatio(updated);
	// };

	const checkEligibilityDate = async (date: string) => {
		try {
			if (!date) return;

			setIsCheckingEligibilityDate(true);

			const response = await onCheckEligibilityDate(date);
			setIsValidEligibilityDate(response.isValidEligibilityDate);

			setIsCheckingEligibilityDate(false);
		} catch (error: any) {
			setIsValidEligibilityDate(false);
			setIsCheckingEligibilityDate(false);

			if (error?.response?.data?.data?.errors?.eligibility_date?.[0]) {
				form.setFields([
					{
						name: 'eligibility_date',
						errors: [error?.response?.data?.data?.errors?.eligibility_date?.[0]],
					},
				]);

				return;
			}

			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	// const checkCapitalRaiseRatio = useDebouncedCallback(async (capitalRaise: number) => {
	// 	if (!capitalRaise) return;

	// 	try {
	// 		setIsCheckingCapitalRaiseRatio(true);

	// 		const response = await onCheckCapitalRaiseRatio({
	// 			eligibility_date: dayjs(form.getFieldValue('eligibility_date')).format('YYYY-MM-DD'),
	// 			capital_raise_ratio: +capitalRaise,
	// 		});

	// 		form.setFieldValue('capital_raise_ratio', +capitalRaise);
	// 		form.setFieldValue('number_of_issued_units', response.ratioResult.numberOfIssuedUnits);
	// 		setTotalCapitalRaise(response.ratioResult.totalCapitalRaise);

	// 		setIsCheckingCapitalRaiseRatio(false);
	// 	} catch (error: any) {
	// 		setIsCheckingCapitalRaiseRatio(false);
	// 		const message =
	// 			error?.response?.data?.data?.errors?.capital_raise_ratio?.[0] ||
	// 			error?.response?.data?.data?.errors?.number_of_issued_units?.[0] ||
	// 			error?.response?.data?.data?.errors?.eligibility_date?.[0] ||
	// 			t('ACTIONS.CHECK_CAPITAL_RATIO_ERROR');

	// 		mapResponseErrors(error, form);

	// 		NotificationService.error(t('COMMON.ERROR'), message);
	// 	}
	// }, 1000);

	const checkCapitalRaiseByUnit = useDebouncedCallback(async (number_of_issued_units: number) => {
		if (!number_of_issued_units) return;

		try {
			setIsCheckingCapitalRaiseRatio(true);

			const response = await onCheckCapitalRaiseByUnits({
				eligibility_date: dayjs(form.getFieldValue('eligibility_date')).format('YYYY-MM-DD'),
				number_of_issued_units: +number_of_issued_units.toFixed(2),
			});

			form.setFieldValue('capital_raise_ratio', response.ratioResult.capitalRaiseRatio);
			form.setFieldValue('number_of_issued_units', response.ratioResult.numberOfIssuedUnits);
			setTotalCapitalRaise(response.ratioResult.totalCapitalRaise);
			setActualCapitalRaiseRatio(+response.ratioResult.capitalRaiseRatio);

			setIsCheckingCapitalRaiseRatio(false);
		} catch (error: any) {
			setIsCheckingCapitalRaiseRatio(false);
			const message =
				error?.response?.data?.data?.errors?.capital_raise_ratio?.[0] ||
				error?.response?.data?.data?.errors?.number_of_issued_units?.[0] ||
				error?.response?.data?.data?.errors?.eligibility_date?.[0] ||
				t('ACTIONS.CHECK_CAPITAL_RATIO_ERROR');

			mapResponseErrors(error, form);

			NotificationService.error(t('COMMON.ERROR'), message);
		}
	}, 1000);

	const onSubmit = async (data: VerificationPayload) => {
		try {
			setIsSubmitting(true);

			const payload: ProfitCapitalizationCreatePayload = {
				otp: data.key,
				capital_raise_ratio: actualCapitalRaiseRatio,
				eligibility_date: dayjs(form.getFieldValue('eligibility_date')).format('YYYY-MM-DD'),
				distribution_date: dayjs(form.getFieldValue('distribution_date')).format('YYYY-MM-DD'),
			};

			editMode ? await onUpdateProfitCapitalization(id!, payload) : await onCreateProfitCapitalization(payload);

			NotificationService.success(
				t('COMMON.SUCCESS'),
				editMode ? t('ACTIONS.PROFIT_CAPITALIZATION_UPDATED_SUCCESSFULLY') : t('ACTIONS.PROFIT_CAPITALIZATION_CREATED_SUCCESSFULLY'),
			);
			navigate('/dashboard/actions/capitals');
		} catch (error: any) {
			setIsSubmitting(false);
			setVerificationModalOpen(false);

			if (error?.status === 422) mapResponseErrors(form, error);

			const message =
				error?.response?.data?.message ||
				(editMode ? t('ACTIONS.PROFIT_CAPITALIZATION_NOT_UPDATED') : t('ACTIONS.PROFIT_CAPITALIZATION_NOT_CREATED'));
			NotificationService.error(t('COMMON.ERROR'), message);
		}
	};

	return isLoading ? (
		<LoadingComponent />
	) : (
		<>
			<Form form={form} onFinish={onSubmit} initialValues={{ capital_raise_ratio: 0, number_of_issued_units: 0 }}>
				{(!hiddenDateFields || editMode) && (
					<Flex align="end" gap={20}>
						<div>
							<DatePickerElement
								label={t('ACTIONS.ELIGIBILITY_DATE')}
								name="eligibility_date"
								rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
								disabled={isCheckingEligibilityDate}
								onChange={(date) => checkEligibilityDate(date?.format('YYYY-MM-DD') || '')}
								isLoading={isCheckingEligibilityDate}
								maxDate={dayjs().subtract(1, 'day')}
							/>
						</div>
					</Flex>
				)}

				<Divider />

				<div style={{ filter: isValidEligibilityDate || hiddenDateFields ? '' : 'grayscale(1)opacity(0.5)' }}>
					<Flex gap={20} wrap>
						<div>
							<Flex align="center" className="mb-1">
								{t('ACTIONS.MINIMUM_CAPITAL_RAISE')}
								<InfoTooltip title={t('ACTIONS.ACTUAL_PERCENTAGE', { value: actualCapitalRaiseRatio })} />
							</Flex>

							{isCheckingCapitalRaiseRatio ? (
								<Skeleton.Button active className="w-100" style={{ height: 40, maxWidth: 'var(--inputWidth)', borderRadius: 'var(--radius)' }} />
							) : (
								<Flex
									gap={10}
									align="center"
									style={{
										padding: '0px 10px',
										border: '1px solid #D0D5DD',
										borderRadius: 'var(--radius)',
										background: '#fafafa',
									}}>
									{/* <Button
									type="primary"
									className="h-max"
									style={{ padding: 7 }}
									onClick={() => modifyCount('minus')}
									disabled={capitalRaiseWatch === 0 || (!isValidEligibilityDate && !hiddenDateFields)}>
									<Icon name="minus" color="#fff" width={10} height={10} />
								</Button> */}

									<Form.Item name="capital_raise_ratio" style={{ minHeight: 'unset' }} className="p-0 mb-0 h-auto" help="">
										<InputNumber
											variant="borderless"
											precision={4}
											min={0}
											step={0.01}
											className="p-0"
											disabled={true}

											// onChange={(value: any) => {
											// 	checkCapitalRaiseRatio(value ? +value.toFixed(4) : 0);
											// }}
										/>
									</Form.Item>

									{/* <Button
									type="primary"
									className="h-max"
									style={{ padding: 7 }}
									disabled={!isValidEligibilityDate && !hiddenDateFields}
									onClick={() => modifyCount('plus')}>
									<Icon name="plus" color="#fff" width={10} height={10} />
								</Button> */}
								</Flex>
							)}
						</div>

						<div>
							<p className="mb-1">{t('ACTIONS.TOTAL_NUMBER_OF_UNIT_ISSUED')}</p>
							<FormNumberElement
								name="number_of_issued_units"
								inputClasses="w-120"
								precision={0}
								formStyles={{ maxWidth: 'unset' }}
								disabled={!isValidEligibilityDate && !hiddenDateFields}
								onChange={(value) => checkCapitalRaiseByUnit(value || 0)}
								addonAfter={
									<Flex gap={20} align="center">
										<p className="mb-0" style={{ fontSize: 15, marginInlineEnd: 20 }}>
											{t('COMMON.SHARES')}
										</p>
										<StatusComponent text={t('ACTIONS.TO_AVOID_FRACTIONS')} color="blue" bordered style={{ fontSize: 14, fontWeight: 500 }} />
									</Flex>
								}
							/>
						</div>
					</Flex>

					<Row gutter={[16, 16]} className="mt-4">
						<Col span={24} md={12}>
							{isCheckingCapitalRaiseRatio ? (
								<>
									<Typography.Text style={{ fontSize: 15, marginBottom: 5, display: 'block' }}>{t('ACTIONS.TOTAL_CAPITAL_RAISE')}</Typography.Text>
									<Skeleton.Button active className="w-100" style={{ height: 40, maxWidth: 'var(--inputWidth)', borderRadius: 'var(--radius)' }} />
								</>
							) : (
								<DisabledElement
									value={<NumberElement value={totalCapitalRaise} decimalScale={2} />}
									label={t('ACTIONS.TOTAL_CAPITAL_RAISE')}
									showPrice
								/>
							)}
						</Col>
						<Col span={24} md={12}>
							{!hiddenDateFields && (
								<DatePickerElement
									name="distribution_date"
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
									label={t('ACTIONS.DISTRIBUTION_DATE')}
									disabled={!isValidEligibilityDate}
									minDate={dayjs()}
								/>
							)}
						</Col>
					</Row>
				</div>

				<Flex wrap gap={20} justify="space-between" align="center" className="mt-5">
					<Checkbox onChange={(event) => setAcknowledge(event.target.checked)}>
						<span className="fw-semibold d-block">{t('ACTIONS.AGREEMENT_TO_TERMS_CAPITALS')}</span>
						<span className="text-light">{t('MORTGAGE.AGREEMENT_2')}</span>
					</Checkbox>

					<Flex gap={20} align="center">
						{!isLoading && !editMode && <ActionCostComponent serviceCost={serviceCost!} />}

						<SuspendedTooltip>
							<Button
								type="primary"
								className="px-4"
								disabled={!acknowledge}
								onClick={async () => {
									try {
										await form.validateFields();
										setVerificationModalOpen(true);
									} catch (error) {}
								}}>
								{editMode ? t('COMMON.UPDATE') : t('COMMON.SUBMIT')}
							</Button>
						</SuspendedTooltip>
					</Flex>
				</Flex>
			</Form>

			<VerificationModal
				otpOnly
				open={verificationModalOpen}
				isLoading={isSubmitting}
				className="confirmationModal"
				onCancel={() => setVerificationModalOpen(false)}
				onVerificationSuccess={onSubmit}
				module={editMode ? 'update_capitalRaise_profit_capitalization' : 'create_capitalRaise_profit_capitalization'}
				note={t('ACTIONS.PROFIT_CAPITALIZATION_NOTE')}
			/>
		</>
	);
};

export default ProfitCapitalizationForm;
